import { Trans, useLingui } from '@lingui/react/macro';
import { Box, Button, Checkbox, CircularProgress, FormControlLabel, FormGroup, Typography } from '@mui/material';
import { type FormEvent, useCallback, useState } from 'react';
import { BaseDialog, type DialogKey, useDialogStore } from '@/components/dialogs';
import { useOneClickTrading } from '../../components/header/user_settings/useOneClickTrading.ts';

const DIALOG_KEY: DialogKey = 'oneClickTradingDialog';

export function OneClickTradingDialog() {
  const { t } = useLingui();
  const { isOneClickEnabled, handleToggleChange, isPending } = useOneClickTrading();
  const close = useDialogStore((state) => state.closeDialog);
  const [localChecked, setLocalChecked] = useState(true);

  const handleDialogClose = useCallback(() => {
    close(DIALOG_KEY);
  }, [close]);

  const handleSwitchChange = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (isOneClickEnabled !== localChecked) {
      handleToggleChange(localChecked);
    }
    handleDialogClose();
  };

  return (
    <BaseDialog
      dialogKey={DIALOG_KEY}
      title={t`One-Click Trading`}
      onSubmit={handleSwitchChange}
      onClose={handleDialogClose}
      actions={
        <Box display="flex" justifyContent="flex-end" width="100%">
          <Button type="submit" variant="contained" size="large" disabled={isPending} fullWidth>
            <Trans>Confirm</Trans>
          </Button>
        </Box>
      }
    >
      <FormGroup>
        <Box display="flex" flexDirection="column" gap={2}>
          <Typography variant="body1">
            <Trans>Approve one-click trading to enjoy a smoother trading experience.</Trans>
          </Typography>

          <Box>
            <FormControlLabel
              control={
                <Checkbox
                  checked={localChecked}
                  onChange={(_, checked) => setLocalChecked(checked)}
                  name="agreeTerms"
                  autoFocus
                  readOnly={isPending}
                />
              }
              label={
                <Box display="flex" alignItems="center" gap={1}>
                  <Typography>
                    <Trans>Enable One-Click Trading</Trans>
                  </Typography>
                  {isPending && <CircularProgress size={16} />}
                </Box>
              }
            />
          </Box>

          <Typography variant="body2" color="text.secondary">
            <Trans>You can change this setting later in the Preferences menu.</Trans>
          </Typography>
        </Box>
      </FormGroup>
    </BaseDialog>
  );
}
